import {
  useEffect,
} from 'react';
import {
  useLocation,
} from 'react-router-dom';

import {
  useRetziaUser,
} from '../hooks/useRetziaUser';
import {
  navigateWithSession,
  replaceWithSession,
} from '../services/sessionNavigation';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChatIcon,
  CourseIcon,
  DoubtIcon,
} from './retzia/RetziaShellIcons';

const RETZ_URL = 'https://retz.mx';

interface LanguageDetails {
  label: string;
  teacher: string;
  role: 'profesor' | 'profesora';
  photo: string;
  photoAlt: string;
  profileUrl: string;
}

const LANGUAGE_DETAILS: Record<
  string,
  LanguageDetails
> = {
  english: {
    label: 'inglés',
    teacher: 'Ana Paula',
    role: 'profesora',
    photo:
      'https://retz.com.mx/wp-content/uploads/2026/07/ana-de-la-torre.webp',
    photoAlt: 'Profesora de inglés',
    profileUrl:
      'https://retz.mx/user/profile.php?id=202',
  },
  german: {
    label: 'alemán',
    teacher: 'Robert',
    role: 'profesor',
    photo:
      'https://retz.com.mx/wp-content/uploads/2026/07/robert-retzinger.webp',
    photoAlt: 'Profesor de alemán',
    profileUrl:
      'https://retz.mx/user/profile.php?id=138',
  },
  french: {
    label: 'francés',
    teacher: 'Daniela',
    role: 'profesora',
    photo:
      'https://retz.com.mx/wp-content/uploads/2026/07/daniela-escobedo.webp',
    photoAlt: 'Profesora de francés',
    profileUrl:
      'https://retz.mx/user/profile.php?id=1865',
  },
};

function LanguageHome() {
  const location = useLocation();

  const language: string =
    location.state?.language ??
    new URLSearchParams(
      location.search
    ).get('language') ??
    'english';

  const details =
    LANGUAGE_DETAILS[language] ??
    LANGUAGE_DETAILS.english;

  const {
    currentUser,
    checking,
  } = useRetziaUser();

  useEffect(() => {
    const label =
      details.label[0].toUpperCase() +
      details.label.slice(1);

    document.title = `${label} | RETZIA`;
  }, [details.label]);

  useEffect(() => {
    if (!checking && !currentUser) {
      replaceWithSession(
        `/login?language=${encodeURIComponent(
          language
        )}&returnTo=language`
      );
    }
  }, [checking, currentUser, language]);

  if (checking || !currentUser) {
    return null;
  }

  const openAction = (
    action: 'conversation' | 'doubt' | 'course'
  ) => {
    const path =
      action === 'conversation'
        ? '/chat'
        : action === 'doubt'
          ? '/grammar'
          : '/taskreviewer';

    navigateWithSession(
      `${path}?language=${language}`
    );
  };

  const supportLabel =
    details.role === 'profesor'
      ? 'Apoyo de tu profesor'
      : 'Apoyo de tu profesora';

  return (
    <div
      className="retzia-language"
      data-retzia-language={language}
    >
      <header className="retzia-language__top">
        <a
          className="retzia-language__retz-logo"
          href={RETZ_URL}
          target="_top"
          aria-label="Volver a RETZ"
        />

        <a
          className="retzia-language__change-language"
          href="/home"
          aria-label="Cambiar idioma"
          onClick={event => {
            event.preventDefault();
            navigateWithSession('/home');
          }}
        >
          <ArrowLeftIcon />
          <span>Cambiar idioma</span>
        </a>
      </header>

      <main className="retzia-language__main">
        <section>
          <div className="retzia-language__hero">
            <span
              className="retzia-language__symbol"
              aria-hidden="true"
            >
              <span className="retzia-language__symbol-mark" />
              <i className="retzia-language__spark" />
            </span>

            <div className="retzia-language__welcome">
              <p className="retzia-language__kicker">
                Estás practicando {details.label}
              </p>

              <h1 className="retzia-language__title">
                ¿Qué quieres hacer hoy?
              </h1>
            </div>
          </div>

          <p className="retzia-language__lead">
            Elige una opción para comenzar. Podrás cambiar de actividad cuando quieras.
          </p>
        </section>

        <section
          className="retzia-language__actions"
          aria-label="Opciones de práctica"
        >
          <button
            className="retzia-language__action-card retzia-language__chat"
            type="button"
            onClick={() =>
              openAction('conversation')
            }
          >
            <span
              className="retzia-language__motif-tail"
              aria-hidden="true"
            />

            <span className="retzia-language__card-top">
              <span className="retzia-language__icon">
                <ChatIcon />
              </span>
              <span className="retzia-language__arrow">
                <ArrowRightIcon />
              </span>
            </span>

            <span className="retzia-language__card-copy">
              <h2>Practicar conversación</h2>
              <span>
                Conversa sobre distintos temas y recibe correcciones mientras practicas.
              </span>
            </span>
          </button>

          <button
            className="retzia-language__action-card retzia-language__doubt"
            type="button"
            onClick={() =>
              openAction('doubt')
            }
          >
            <span className="retzia-language__card-top">
              <span className="retzia-language__icon">
                <DoubtIcon />
              </span>
              <span className="retzia-language__arrow">
                <ArrowRightIcon />
              </span>
            </span>

            <span className="retzia-language__card-copy">
              <h2>Resolver una duda</h2>
              <span>
                Pregunta sobre gramática o vocabulario y recibe una explicación clara.
              </span>
            </span>
          </button>

          <button
            className="retzia-language__action-card retzia-language__course"
            type="button"
            onClick={() =>
              openAction('course')
            }
          >
            <span className="retzia-language__card-top">
              <span className="retzia-language__icon">
                <CourseIcon />
              </span>
              <span className="retzia-language__arrow">
                <ArrowRightIcon />
              </span>
            </span>

            <span className="retzia-language__card-copy">
              <h2>Realizar actividad del curso</h2>
              <span>
                Sigue la instrucción de tu clase y completa la actividad con apoyo de RETZIA.
              </span>
            </span>
          </button>
        </section>

        <section className="retzia-language__teacher-card">
          <div className="retzia-language__teacher-main">
            <img
              className="retzia-language__teacher-photo"
              src={details.photo}
              alt={details.photoAlt}
            />

            <div className="retzia-language__teacher-copy">
              <p className="retzia-language__teacher-label">
                {supportLabel}
              </p>
              <h3>{details.teacher}</h3>
              <p>
                Si necesitas ayuda adicional, puedes escribirle directamente desde tu perfil.
              </p>
            </div>
          </div>

          <button
            className="retzia-language__teacher-action"
            type="button"
            aria-label={`Contactar a ${details.teacher}`}
            onClick={() =>
              window.open(
                details.profileUrl,
                '_blank',
                'noopener,noreferrer'
              )
            }
          >
            Contactar
            <ArrowRightIcon />
          </button>
        </section>
      </main>
    </div>
  );
}

export default LanguageHome;
